/**
 * CompanySettingsPage - Admin settings at /company/:slug/settings
 *
 * Tabs: General (name, invite code), Departments, Teams.
 * Admin-only - redirects non-admins back to company dashboard.
 * BtoB-oriented language only (no fortune-telling or astrology references).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import companyService from '../../services/companyService';
import BillingTab from '../../components/company/BillingTab';

/* ================================================================== */
/*  Constants                                                          */
/* ================================================================== */

const TABS = {
  GENERAL: 'general',
  DEPARTMENTS: 'departments',
  TEAMS: 'teams',
  POSITIONS: 'positions',
  BILLING: 'billing',
};

/* ================================================================== */
/*  Keyframes                                                          */
/* ================================================================== */

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const toastSlide = keyframes`
  0%   { opacity: 0; transform: translateY(16px); }
  10%  { opacity: 1; transform: translateY(0); }
  90%  { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-8px); }
`;

/* ================================================================== */
/*  Styled components                                                  */
/* ================================================================== */

const PageContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 48px 24px 80px;
  animation: ${fadeIn} 0.35s ease-out;

  @media (max-width: ${(p) => p.theme.breakpoints?.mobile || '640px'}) {
    padding: 24px 16px 64px;
  }
`;

const Toast = styled.div`
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  background: ${(p) => p.theme.colors.success || '#10B981'};
  color: #fff;
  padding: 10px 24px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  font-size: 0.85rem;
  font-weight: 600;
  pointer-events: none;
  z-index: 1000;
  animation: ${toastSlide} 2s ease forwards;
`;

/* ---- Breadcrumb ---- */

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.textSecondary};
  flex-wrap: wrap;
`;

const BreadcrumbLink = styled(Link)`
  color: ${(p) => p.theme.colors.primary};
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const BreadcrumbSep = styled.span`
  color: ${(p) => p.theme.colors.textSecondary};
  user-select: none;
`;

const BreadcrumbCurrent = styled.span`
  color: ${(p) => p.theme.colors.text};
  font-weight: 600;
`;

/* ---- Page title ---- */

const PageTitle = styled.h1`
  font-size: 1.6rem;
  font-weight: 800;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 28px 0;

  @media (max-width: ${(p) => p.theme.breakpoints?.mobile || '640px'}) {
    font-size: 1.3rem;
  }
`;

/* ---- Tabs ---- */

const TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
  margin-bottom: 28px;
  overflow-x: auto;
`;

const Tab = styled.button`
  padding: 10px 20px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${(p) => (p.$active ? p.theme.colors.primary : 'transparent')};
  color: ${(p) => (p.$active ? p.theme.colors.text : p.theme.colors.textSecondary)};
  font-size: 0.88rem;
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
  white-space: nowrap;

  &:hover {
    color: ${(p) => p.theme.colors.text};
  }
`;

/* ---- Section card ---- */

const SectionCard = styled.div`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius?.lg || '12px'};
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 16px 0;
`;

/* ---- Form elements ---- */

const FormRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 16px;

  @media (max-width: ${(p) => p.theme.breakpoints?.mobile || '640px'}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TextInput = styled.input`
  flex: 1;
  padding: 8px 14px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  background: ${(p) => p.theme.colors.bgTertiary};
  border: 1px solid ${(p) => p.theme.colors.border};
  color: ${(p) => p.theme.colors.text};
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s;
  &:focus { border-color: ${(p) => p.theme.colors.primary}; }
  &::placeholder { color: ${(p) => p.theme.colors.textSecondary}; opacity: 0.6; }
`;

const SelectInput = styled.select`
  flex: 1;
  padding: 8px 12px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  background: ${(p) => p.theme.colors.bgTertiary};
  border: 1px solid ${(p) => p.theme.colors.border};
  color: ${(p) => p.theme.colors.text};
  font-size: 0.9rem;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s;
  &:focus { border-color: ${(p) => p.theme.colors.primary}; }
`;

const PrimaryButton = styled.button`
  padding: 8px 20px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  background: ${(p) => p.theme.colors.primary};
  color: #fff;
  border: none;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
  &:hover { background: ${(p) => p.theme.colors.secondary}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SecondaryButton = styled.button`
  padding: 8px 20px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  background: transparent;
  border: 1px solid ${(p) => p.theme.colors.border};
  color: ${(p) => p.theme.colors.text};
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
  white-space: nowrap;
  &:hover {
    border-color: ${(p) => p.theme.colors.primary};
    color: ${(p) => p.theme.colors.primary};
  }
`;

const DangerButton = styled.button`
  padding: 6px 14px;
  border-radius: ${(p) => p.theme.borderRadius?.sm || '4px'};
  background: transparent;
  border: 1px solid ${(p) => p.theme.colors.error || '#EF4444'};
  color: ${(p) => p.theme.colors.error || '#EF4444'};
  font-weight: 500;
  font-size: 0.78rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  white-space: nowrap;
  &:hover {
    background: ${(p) => p.theme.colors.error || '#EF4444'};
    color: #fff;
  }
`;

const SmallButton = styled.button`
  padding: 4px 12px;
  border-radius: ${(p) => p.theme.borderRadius?.sm || '4px'};
  background: transparent;
  border: 1px solid ${(p) => p.theme.colors.border};
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
  white-space: nowrap;
  &:hover {
    border-color: ${(p) => p.theme.colors.primary};
    color: ${(p) => p.theme.colors.primary};
  }
`;

/* ---- List items ---- */

const ListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${(p) => p.theme.colors.bgTertiary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  margin-bottom: 8px;
  transition: border-color 0.2s;

  &:last-child { margin-bottom: 0; }
`;

const ListItemName = styled.span`
  flex: 1;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.text};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ListItemSub = styled.span`
  font-size: 0.78rem;
  color: ${(p) => p.theme.colors.textSecondary};
  white-space: nowrap;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const InlineEditInput = styled.input`
  flex: 1;
  padding: 4px 10px;
  border-radius: ${(p) => p.theme.borderRadius?.sm || '4px'};
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.primary};
  color: ${(p) => p.theme.colors.text};
  font-size: 0.88rem;
  outline: none;
`;

/* ---- Department group header ---- */

const DeptGroupHeader = styled.h3`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textSecondary};
  margin: 20px 0 10px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid ${(p) => p.theme.colors.border};

  &:first-child { margin-top: 0; }
`;

/* ---- Confirm overlay ---- */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 900;
  padding: 24px;
`;

const Dialog = styled.div`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius?.lg || '12px'};
  padding: 28px;
  max-width: 400px;
  width: 100%;
`;

const DialogTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 12px 0;
`;

const DialogText = styled.p`
  font-size: 0.88rem;
  color: ${(p) => p.theme.colors.textSecondary};
  margin: 0 0 20px 0;
  line-height: 1.5;
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const DialogButton = styled.button`
  padding: 8px 20px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  font-size: 0.85rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 0.2s;

  ${(p) =>
    p.$primary
      ? `
    background: ${p.$danger ? (p.theme.colors.error || '#EF4444') : p.theme.colors.primary};
    color: #fff;
    &:hover { opacity: 0.85; }
  `
      : `
    background: ${p.theme.colors.bgTertiary};
    color: ${p.theme.colors.text};
    border: 1px solid ${p.theme.colors.border};
    &:hover { border-color: ${p.theme.colors.primary}; }
  `}
`;

/* ---- Add form ---- */

const AddForm = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${(p) => p.theme.colors.border};

  @media (max-width: ${(p) => p.theme.breakpoints?.mobile || '640px'}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

/* ---- Invite code display ---- */

const InviteCodeBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: ${(p) => p.theme.colors.bgTertiary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  margin-bottom: 12px;
`;

const InviteCode = styled.span`
  font-family: ${(p) => p.theme.fonts?.mono};
  font-size: 1rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  letter-spacing: 1px;
  flex: 1;
`;

/* ---- Empty state ---- */

const EmptyState = styled.div`
  text-align: center;
  padding: 24px;
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: 0.88rem;
  line-height: 1.6;
`;

/* ---- Loading skeleton ---- */

const SkeletonBlock = styled.div`
  background: linear-gradient(
    90deg,
    ${(p) => p.theme.colors.bgTertiary} 25%,
    ${(p) => p.theme.colors.bgSecondary} 50%,
    ${(p) => p.theme.colors.bgTertiary} 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s infinite;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  height: ${(p) => p.$h || '20px'};
  width: ${(p) => p.$w || '100%'};
  margin-bottom: ${(p) => p.$mb || '0'};
`;

/* ---- Error state ---- */

const ErrorContainer = styled.div`
  text-align: center;
  padding: 64px 24px;
`;

const ErrorTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 12px 0;
`;

const ErrorMessage = styled.p`
  font-size: 0.9rem;
  color: ${(p) => p.theme.colors.textSecondary};
  margin: 0 0 24px 0;
`;

const RetryButton = styled.button`
  padding: 10px 24px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  background: ${(p) => p.theme.colors.primary};
  color: #fff;
  border: none;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: ${(p) => p.theme.colors.secondary}; }
`;

/* ================================================================== */
/*  Loading skeleton                                                   */
/* ================================================================== */

function LoadingSkeleton() {
  return (
    <PageContainer>
      <SkeletonBlock $h="16px" $w="200px" $mb="24px" />
      <SkeletonBlock $h="32px" $w="180px" $mb="28px" />
      <SkeletonBlock $h="40px" $w="100%" $mb="28px" />
      <SectionCard>
        <SkeletonBlock $h="14px" $w="120px" $mb="16px" />
        <SkeletonBlock $h="36px" $w="100%" $mb="12px" />
        <SkeletonBlock $h="36px" $w="60%" />
      </SectionCard>
    </PageContainer>
  );
}

/* ================================================================== */
/*  Main component                                                     */
/* ================================================================== */

function CompanySettingsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const { user, isAuthenticated } = useAuth();

  /* ---- State ---- */
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toastText, setToastText] = useState('');

  // Support ?tab=billing from URL
  const initialTab = searchParams.get('tab') === 'billing' ? TABS.BILLING : TABS.GENERAL;
  const [activeTab, setActiveTab] = useState(initialTab);

  /* ---- General tab state ---- */
  const [companyName, setCompanyName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [regenerateConfirm, setRegenerateConfirm] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  /* ---- Departments tab state ---- */
  const [newDeptName, setNewDeptName] = useState('');
  const [addingDept, setAddingDept] = useState(false);
  const [editingDeptId, setEditingDeptId] = useState(null);
  const [editingDeptName, setEditingDeptName] = useState('');
  const [deleteDeptConfirm, setDeleteDeptConfirm] = useState(null);

  /* ---- Teams tab state ---- */
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDeptId, setNewTeamDeptId] = useState('');
  const [addingTeam, setAddingTeam] = useState(false);
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editingTeamName, setEditingTeamName] = useState('');
  const [editingTeamDeptId, setEditingTeamDeptId] = useState('');
  const [deleteTeamConfirm, setDeleteTeamConfirm] = useState(null);

  /* ---- Positions tab state ---- */
  const [positions, setPositions] = useState([]);
  const [newPositionName, setNewPositionName] = useState('');
  const [savingPositions, setSavingPositions] = useState(false);

  /* ---- Derived ---- */
  const isAdmin = useMemo(
    () =>
      company?.members?.some(
        (m) => m.user_id === user?.id && m.role === 'admin'
      ) ?? false,
    [company, user]
  );

  const departments = useMemo(() => company?.departments || [], [company]);
  const allTeams = useMemo(() => company?.teams || [], [company]);

  /* ---- Department drag & drop ---- */
  const deptDragItem = React.useRef(null);
  const deptDragOverItem = React.useRef(null);

  /* ---- Toast helper ---- */
  const showToast = useCallback((msg) => {
    setToastText(msg);
    setTimeout(() => setToastText(''), 2200);
  }, []);

  /* ---- Data fetching ---- */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await companyService.getCompany(slug);
      setCompany(data);
      setCompanyName(data.name || '');
      setPositions(data.settings?.positions || []);
    } catch (err) {
      setError(err.message || t('データの取得に失敗しました', 'Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ---- Admin gate ---- */
  useEffect(() => {
    if (!loading && company && !isAdmin) {
      navigate(`/company/${slug}`, { replace: true });
    }
  }, [loading, company, isAdmin, navigate, slug]);

  /* ================================================================ */
  /*  General tab handlers                                             */
  /* ================================================================ */

  const handleSaveName = useCallback(async () => {
    if (!companyName.trim() || companyName === company?.name) return;
    setSavingName(true);
    try {
      const updated = await companyService.updateCompany(slug, {
        name: companyName.trim(),
      });
      setCompany((prev) => ({ ...prev, ...updated }));
      showToast(t('組織名を更新しました', 'Company name updated'));
    } catch (err) {
      showToast(err.message || t('更新に失敗しました', 'Update failed'));
    } finally {
      setSavingName(false);
    }
  }, [companyName, company, slug, t, showToast]);

  const handleRegenerateInviteCode = useCallback(async () => {
    setRegenerating(true);
    try {
      const result = await companyService.regenerateInviteCode(slug);
      setCompany((prev) => ({
        ...prev,
        invite_code: result.invite_code || result.code,
      }));
      showToast(t('招待コードを再発行しました', 'Invite code regenerated'));
    } catch (err) {
      showToast(err.message || t('再発行に失敗しました', 'Regeneration failed'));
    } finally {
      setRegenerating(false);
      setRegenerateConfirm(false);
    }
  }, [slug, t, showToast]);

  /* ================================================================ */
  /*  Department handlers                                              */
  /* ================================================================ */

  const handleDeptDrop = useCallback(async () => {
    if (deptDragItem.current === null || deptDragOverItem.current === null) return;
    if (deptDragItem.current === deptDragOverItem.current) return;

    const reordered = [...departments];
    const [dragged] = reordered.splice(deptDragItem.current, 1);
    reordered.splice(deptDragOverItem.current, 0, dragged);

    setCompany((prev) => ({ ...prev, departments: reordered }));

    deptDragItem.current = null;
    deptDragOverItem.current = null;

    try {
      await companyService.reorderDepartments(slug, reordered.map((d) => d.id));
      showToast(t('部署の順序を更新しました', 'Department order updated'));
    } catch (err) {
      showToast(err.message || t('並び替えに失敗しました', 'Reorder failed'));
    }
  }, [departments, slug, t, showToast]);

  const handleAddDepartment = useCallback(async () => {
    if (!newDeptName.trim()) return;
    setAddingDept(true);
    try {
      const dept = await companyService.createDepartment(slug, newDeptName.trim());
      setCompany((prev) => ({
        ...prev,
        departments: [...(prev.departments || []), dept],
      }));
      setNewDeptName('');
      showToast(t('部署を追加しました', 'Department added'));
    } catch (err) {
      showToast(err.message || t('追加に失敗しました', 'Failed to add'));
    } finally {
      setAddingDept(false);
    }
  }, [newDeptName, slug, t, showToast]);

  const handleSaveDepartment = useCallback(
    async (deptId) => {
      if (!editingDeptName.trim()) return;
      try {
        const updated = await companyService.updateDepartment(slug, deptId, {
          name: editingDeptName.trim(),
        });
        setCompany((prev) => ({
          ...prev,
          departments: (prev.departments || []).map((d) =>
            d.id === deptId ? { ...d, ...updated } : d
          ),
        }));
        setEditingDeptId(null);
        showToast(t('部署名を更新しました', 'Department updated'));
      } catch (err) {
        showToast(err.message || t('更新に失敗しました', 'Update failed'));
      }
    },
    [editingDeptName, slug, t, showToast]
  );

  const handleDeleteDepartment = useCallback(async () => {
    if (!deleteDeptConfirm) return;
    try {
      await companyService.deleteDepartment(slug, deleteDeptConfirm.id);
      setCompany((prev) => ({
        ...prev,
        departments: (prev.departments || []).filter(
          (d) => d.id !== deleteDeptConfirm.id
        ),
      }));
      showToast(t('部署を削除しました', 'Department deleted'));
    } catch (err) {
      showToast(err.message || t('削除に失敗しました', 'Delete failed'));
    } finally {
      setDeleteDeptConfirm(null);
    }
  }, [deleteDeptConfirm, slug, t, showToast]);

  /* ================================================================ */
  /*  Team handlers                                                    */
  /* ================================================================ */

  const handleAddTeam = useCallback(async () => {
    if (!newTeamName.trim()) return;
    setAddingTeam(true);
    try {
      const team = await companyService.createTeam(
        slug,
        newTeamName.trim(),
        newTeamDeptId || null
      );
      setCompany((prev) => ({
        ...prev,
        teams: [...(prev.teams || []), team],
      }));
      setNewTeamName('');
      setNewTeamDeptId('');
      showToast(t('チームを追加しました', 'Team added'));
    } catch (err) {
      showToast(err.message || t('追加に失敗しました', 'Failed to add'));
    } finally {
      setAddingTeam(false);
    }
  }, [newTeamName, newTeamDeptId, slug, t, showToast]);

  const handleSaveTeam = useCallback(
    async (teamId) => {
      if (!editingTeamName.trim()) return;
      try {
        const updated = await companyService.updateTeam(slug, teamId, {
          name: editingTeamName.trim(),
          department_id: editingTeamDeptId || null,
        });
        setCompany((prev) => ({
          ...prev,
          teams: (prev.teams || []).map((tm) =>
            tm.id === teamId ? { ...tm, ...updated } : tm
          ),
        }));
        setEditingTeamId(null);
        showToast(t('チームを更新しました', 'Team updated'));
      } catch (err) {
        showToast(err.message || t('更新に失敗しました', 'Update failed'));
      }
    },
    [editingTeamName, editingTeamDeptId, slug, t, showToast]
  );

  const handleDeleteTeam = useCallback(async () => {
    if (!deleteTeamConfirm) return;
    try {
      await companyService.deleteTeam(slug, deleteTeamConfirm.id);
      setCompany((prev) => ({
        ...prev,
        teams: (prev.teams || []).filter(
          (tm) => tm.id !== deleteTeamConfirm.id
        ),
      }));
      showToast(t('チームを削除しました', 'Team deleted'));
    } catch (err) {
      showToast(err.message || t('削除に失敗しました', 'Delete failed'));
    } finally {
      setDeleteTeamConfirm(null);
    }
  }, [deleteTeamConfirm, slug, t, showToast]);

  /* ================================================================ */
  /*  Position handlers                                               */
  /* ================================================================ */

  const handleAddPosition = useCallback(() => {
    const name = newPositionName.trim();
    if (!name || positions.includes(name)) return;
    setPositions((prev) => [...prev, name]);
    setNewPositionName('');
  }, [newPositionName, positions]);

  const handleDeletePosition = useCallback((idx) => {
    setPositions((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleMovePosition = useCallback((idx, dir) => {
    setPositions((prev) => {
      const arr = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return arr;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });
  }, []);

  const handleSavePositions = useCallback(async () => {
    setSavingPositions(true);
    try {
      await companyService.updatePositions(slug, positions);
      setCompany((prev) => ({
        ...prev,
        settings: { ...(prev.settings || {}), positions },
      }));
      showToast(t('役職プリセットを保存しました', 'Position presets saved'));
    } catch (err) {
      showToast(err.message || t('保存に失敗しました', 'Save failed'));
    } finally {
      setSavingPositions(false);
    }
  }, [positions, slug, t, showToast]);

  /* ---- Group teams by department ---- */
  const teamsByDept = useMemo(() => {
    const grouped = {};
    const unassigned = [];
    allTeams.forEach((tm) => {
      if (tm.department_id) {
        if (!grouped[tm.department_id]) grouped[tm.department_id] = [];
        grouped[tm.department_id].push(tm);
      } else {
        unassigned.push(tm);
      }
    });
    return { grouped, unassigned };
  }, [allTeams]);

  /* ================================================================ */
  /*  Render states                                                    */
  /* ================================================================ */

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>{t('ログインが必要です', 'Login Required')}</ErrorTitle>
          <ErrorMessage>{t('このページを利用するにはログインまたは新規登録が必要です。', 'Please log in or sign up to access this page.')}</ErrorMessage>
          <RetryButton onClick={() => navigate('/')}>
            {t('ログイン / 新規登録', 'Login / Sign Up')}
          </RetryButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (loading) return <LoadingSkeleton />;

  if (error) {
    const isAuthError = error === 'Not authenticated' || error === 'Invalid or expired token';
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>{isAuthError ? t('ログインが必要です', 'Login Required') : t('エラーが発生しました', 'An Error Occurred')}</ErrorTitle>
          <ErrorMessage>{isAuthError ? t('このページを利用するにはログインまたは新規登録が必要です。', 'Please log in or sign up to access this page.') : error}</ErrorMessage>
          <RetryButton onClick={isAuthError ? () => navigate('/') : fetchData}>
            {isAuthError ? t('ログイン / 新規登録', 'Login / Sign Up') : t('再試行', 'Retry')}
          </RetryButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (!company || !isAdmin) return null;

  /* ================================================================ */
  /*  Main render                                                      */
  /* ================================================================ */

  return (
    <PageContainer>
      {toastText && <Toast>{toastText}</Toast>}

      {/* ──── Breadcrumb ──── */}
      <Breadcrumb>
        <BreadcrumbLink to={`/company/${slug}`}>
          {company.name || t('組織', 'Company')}
        </BreadcrumbLink>
        <BreadcrumbSep>/</BreadcrumbSep>
        <BreadcrumbCurrent>{t('設定', 'Settings')}</BreadcrumbCurrent>
      </Breadcrumb>

      <PageTitle>{t('設定', 'Settings')}</PageTitle>

      {/* ──── Tab bar ──── */}
      <TabBar>
        <Tab
          $active={activeTab === TABS.GENERAL}
          onClick={() => setActiveTab(TABS.GENERAL)}
        >
          {t('基本情報', 'General')}
        </Tab>
        <Tab
          $active={activeTab === TABS.DEPARTMENTS}
          onClick={() => setActiveTab(TABS.DEPARTMENTS)}
        >
          {t('部署管理', 'Departments')}
        </Tab>
        <Tab
          $active={activeTab === TABS.TEAMS}
          onClick={() => setActiveTab(TABS.TEAMS)}
        >
          {t('チーム管理', 'Teams')}
        </Tab>
        <Tab
          $active={activeTab === TABS.POSITIONS}
          onClick={() => setActiveTab(TABS.POSITIONS)}
        >
          {t('役職', 'Positions')}
        </Tab>
        <Tab
          $active={activeTab === TABS.BILLING}
          onClick={() => setActiveTab(TABS.BILLING)}
        >
          {t('課金', 'Billing')}
        </Tab>
      </TabBar>

      {/* ══════════════ Tab 1: General ══════════════ */}
      {activeTab === TABS.GENERAL && (
        <>
          <SectionCard>
            <SectionTitle>{t('組織名', 'Company Name')}</SectionTitle>
            <FormRow>
              <TextInput
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder={t('組織名を入力', 'Enter company name')}
              />
              <PrimaryButton
                onClick={handleSaveName}
                disabled={
                  savingName ||
                  !companyName.trim() ||
                  companyName === company.name
                }
              >
                {savingName
                  ? t('保存中...', 'Saving...')
                  : t('保存', 'Save')}
              </PrimaryButton>
            </FormRow>
          </SectionCard>

          <SectionCard>
            <SectionTitle>{t('招待コード', 'Invite Code')}</SectionTitle>
            <InviteCodeBox>
              <InviteCode>{company.invite_code || '--'}</InviteCode>
            </InviteCodeBox>
            <SecondaryButton onClick={() => setRegenerateConfirm(true)}>
              {t('招待コードを再発行', 'Regenerate Invite Code')}
            </SecondaryButton>
          </SectionCard>
        </>
      )}

      {/* ══════════════ Tab 2: Departments ══════════════ */}
      {activeTab === TABS.DEPARTMENTS && (
        <SectionCard>
          <SectionTitle>{t('部署一覧', 'Department List')}</SectionTitle>

          {departments.length === 0 ? (
            <EmptyState>
              {t(
                '部署がまだ登録されていません。',
                'No departments registered yet.'
              )}
            </EmptyState>
          ) : (
            departments.map((dept, dIdx) => (
              <ListItem
                key={dept.id}
                draggable
                onDragStart={() => { deptDragItem.current = dIdx; }}
                onDragOver={(e) => { e.preventDefault(); deptDragOverItem.current = dIdx; }}
                onDrop={handleDeptDrop}
                style={{ cursor: 'grab' }}
              >
                {editingDeptId === dept.id ? (
                  <>
                    <InlineEditInput
                      type="text"
                      value={editingDeptName}
                      onChange={(e) => setEditingDeptName(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveDepartment(dept.id);
                        if (e.key === 'Escape') setEditingDeptId(null);
                      }}
                    />
                    <ActionGroup>
                      <SmallButton onClick={() => handleSaveDepartment(dept.id)}>
                        {t('保存', 'Save')}
                      </SmallButton>
                      <SmallButton onClick={() => setEditingDeptId(null)}>
                        {t('キャンセル', 'Cancel')}
                      </SmallButton>
                    </ActionGroup>
                  </>
                ) : (
                  <>
                    <ListItemName>{dept.name}</ListItemName>
                    <ActionGroup>
                      <SmallButton
                        onClick={() => {
                          setEditingDeptId(dept.id);
                          setEditingDeptName(dept.name);
                        }}
                      >
                        {t('編集', 'Edit')}
                      </SmallButton>
                      <DangerButton
                        onClick={() => setDeleteDeptConfirm(dept)}
                      >
                        {t('削除', 'Delete')}
                      </DangerButton>
                    </ActionGroup>
                  </>
                )}
              </ListItem>
            ))
          )}

          <AddForm>
            <TextInput
              type="text"
              placeholder={t('新しい部署名', 'New department name')}
              value={newDeptName}
              onChange={(e) => setNewDeptName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddDepartment();
              }}
              disabled={addingDept}
            />
            <PrimaryButton
              onClick={handleAddDepartment}
              disabled={addingDept || !newDeptName.trim()}
            >
              {addingDept
                ? t('追加中...', 'Adding...')
                : t('追加', 'Add')}
            </PrimaryButton>
          </AddForm>
        </SectionCard>
      )}

      {/* ══════════════ Tab 3: Teams ══════════════ */}
      {activeTab === TABS.TEAMS && (
        <SectionCard>
          <SectionTitle>{t('チーム一覧', 'Team List')}</SectionTitle>

          {allTeams.length === 0 ? (
            <EmptyState>
              {t(
                'チームがまだ登録されていません。',
                'No teams registered yet.'
              )}
            </EmptyState>
          ) : (
            <>
              {/* Teams grouped by department */}
              {departments.map((dept) => {
                const deptTeams = teamsByDept.grouped[dept.id] || [];
                if (deptTeams.length === 0) return null;
                return (
                  <div key={dept.id}>
                    <DeptGroupHeader>{dept.name}</DeptGroupHeader>
                    {deptTeams.map((team) => (
                      <TeamListItem
                        key={team.id}
                        team={team}
                        departments={departments}
                        isEditing={editingTeamId === team.id}
                        editingName={editingTeamName}
                        editingDeptId={editingTeamDeptId}
                        onEditStart={() => {
                          setEditingTeamId(team.id);
                          setEditingTeamName(team.name);
                          setEditingTeamDeptId(team.department_id || '');
                        }}
                        onEditCancel={() => setEditingTeamId(null)}
                        onNameChange={setEditingTeamName}
                        onDeptChange={setEditingTeamDeptId}
                        onSave={() => handleSaveTeam(team.id)}
                        onDelete={() => setDeleteTeamConfirm(team)}
                        t={t}
                      />
                    ))}
                  </div>
                );
              })}

              {/* Unassigned teams */}
              {teamsByDept.unassigned.length > 0 && (
                <div>
                  {departments.length > 0 && (
                    <DeptGroupHeader>
                      {t('未所属チーム', 'Unassigned Teams')}
                    </DeptGroupHeader>
                  )}
                  {teamsByDept.unassigned.map((team) => (
                    <TeamListItem
                      key={team.id}
                      team={team}
                      departments={departments}
                      isEditing={editingTeamId === team.id}
                      editingName={editingTeamName}
                      editingDeptId={editingTeamDeptId}
                      onEditStart={() => {
                        setEditingTeamId(team.id);
                        setEditingTeamName(team.name);
                        setEditingTeamDeptId(team.department_id || '');
                      }}
                      onEditCancel={() => setEditingTeamId(null)}
                      onNameChange={setEditingTeamName}
                      onDeptChange={setEditingTeamDeptId}
                      onSave={() => handleSaveTeam(team.id)}
                      onDelete={() => setDeleteTeamConfirm(team)}
                      t={t}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          <AddForm>
            <TextInput
              type="text"
              placeholder={t('新しいチーム名', 'New team name')}
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddTeam();
              }}
              disabled={addingTeam}
            />
            <SelectInput
              value={newTeamDeptId}
              onChange={(e) => setNewTeamDeptId(e.target.value)}
              disabled={addingTeam}
              style={{ maxWidth: 200 }}
            >
              <option value="">{t('部署なし', 'No department')}</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </SelectInput>
            <PrimaryButton
              onClick={handleAddTeam}
              disabled={addingTeam || !newTeamName.trim()}
            >
              {addingTeam
                ? t('追加中...', 'Adding...')
                : t('追加', 'Add')}
            </PrimaryButton>
          </AddForm>
        </SectionCard>
      )}

      {/* ══════════════ Tab 4: Positions ══════════════ */}
      {activeTab === TABS.POSITIONS && (
        <SectionCard>
          <SectionTitle>{t('役職プリセット', 'Position Presets')}</SectionTitle>

          {positions.length === 0 ? (
            <EmptyState>
              {t(
                '役職がまだ登録されていません。組織参加時に選択できる役職を追加してください。',
                'No positions registered yet. Add positions that members can select when joining.'
              )}
            </EmptyState>
          ) : (
            positions.map((pos, idx) => (
              <ListItem key={idx}>
                <ListItemName>{pos}</ListItemName>
                <ActionGroup>
                  <SmallButton
                    onClick={() => handleMovePosition(idx, -1)}
                    disabled={idx === 0}
                    title={t('上へ', 'Move up')}
                  >
                    ↑
                  </SmallButton>
                  <SmallButton
                    onClick={() => handleMovePosition(idx, 1)}
                    disabled={idx === positions.length - 1}
                    title={t('下へ', 'Move down')}
                  >
                    ↓
                  </SmallButton>
                  <DangerButton onClick={() => handleDeletePosition(idx)}>
                    {t('削除', 'Delete')}
                  </DangerButton>
                </ActionGroup>
              </ListItem>
            ))
          )}

          <AddForm>
            <TextInput
              type="text"
              placeholder={t('新しい役職名（例: 部長, 課長）', 'New position (e.g. Manager, Lead)')}
              value={newPositionName}
              onChange={(e) => setNewPositionName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddPosition();
              }}
            />
            <PrimaryButton
              onClick={handleAddPosition}
              disabled={!newPositionName.trim()}
            >
              {t('追加', 'Add')}
            </PrimaryButton>
          </AddForm>

          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <PrimaryButton
              onClick={handleSavePositions}
              disabled={savingPositions}
            >
              {savingPositions
                ? t('保存中...', 'Saving...')
                : t('保存', 'Save')}
            </PrimaryButton>
          </div>
        </SectionCard>
      )}

      {/* ══════════════ Tab 5: Billing ══════════════ */}
      {activeTab === TABS.BILLING && (
        <BillingTab slug={slug} />
      )}

      {/* ──── Regenerate invite code confirm ──── */}
      {regenerateConfirm && (
        <Overlay onClick={() => setRegenerateConfirm(false)}>
          <Dialog onClick={(e) => e.stopPropagation()}>
            <DialogTitle>
              {t('招待コードの再発行', 'Regenerate Invite Code')}
            </DialogTitle>
            <DialogText>
              {t(
                '現在の招待コードは無効になり、新しいコードが発行されます。この操作は取り消せません。',
                'The current invite code will be invalidated and a new one will be generated. This action cannot be undone.'
              )}
            </DialogText>
            <DialogActions>
              <DialogButton onClick={() => setRegenerateConfirm(false)}>
                {t('キャンセル', 'Cancel')}
              </DialogButton>
              <DialogButton
                $primary
                onClick={handleRegenerateInviteCode}
                disabled={regenerating}
              >
                {regenerating
                  ? t('発行中...', 'Generating...')
                  : t('再発行', 'Regenerate')}
              </DialogButton>
            </DialogActions>
          </Dialog>
        </Overlay>
      )}

      {/* ──── Delete department confirm ──── */}
      {deleteDeptConfirm && (
        <Overlay onClick={() => setDeleteDeptConfirm(null)}>
          <Dialog onClick={(e) => e.stopPropagation()}>
            <DialogTitle>
              {t('部署を削除', 'Delete Department')}
            </DialogTitle>
            <DialogText>
              {t(
                `「${deleteDeptConfirm.name}」を削除しますか？この部署に所属するチームは未所属になります。`,
                `Delete "${deleteDeptConfirm.name}"? Teams in this department will become unassigned.`
              )}
            </DialogText>
            <DialogActions>
              <DialogButton onClick={() => setDeleteDeptConfirm(null)}>
                {t('キャンセル', 'Cancel')}
              </DialogButton>
              <DialogButton $primary $danger onClick={handleDeleteDepartment}>
                {t('削除', 'Delete')}
              </DialogButton>
            </DialogActions>
          </Dialog>
        </Overlay>
      )}

      {/* ──── Delete team confirm ──── */}
      {deleteTeamConfirm && (
        <Overlay onClick={() => setDeleteTeamConfirm(null)}>
          <Dialog onClick={(e) => e.stopPropagation()}>
            <DialogTitle>
              {t('チームを削除', 'Delete Team')}
            </DialogTitle>
            <DialogText>
              {t(
                `「${deleteTeamConfirm.name}」を削除しますか？チームメンバーは未所属になります。`,
                `Delete "${deleteTeamConfirm.name}"? Team members will become unassigned.`
              )}
            </DialogText>
            <DialogActions>
              <DialogButton onClick={() => setDeleteTeamConfirm(null)}>
                {t('キャンセル', 'Cancel')}
              </DialogButton>
              <DialogButton $primary $danger onClick={handleDeleteTeam}>
                {t('削除', 'Delete')}
              </DialogButton>
            </DialogActions>
          </Dialog>
        </Overlay>
      )}
    </PageContainer>
  );
}

/* ================================================================== */
/*  Sub-components                                                     */
/* ================================================================== */

/** Reusable team list item with inline editing */
function TeamListItem({
  team,
  departments,
  isEditing,
  editingName,
  editingDeptId,
  onEditStart,
  onEditCancel,
  onNameChange,
  onDeptChange,
  onSave,
  onDelete,
  t,
}) {
  const deptName = useMemo(() => {
    if (!team.department_id) return null;
    const dept = departments.find((d) => d.id === team.department_id);
    return dept?.name || null;
  }, [team, departments]);

  if (isEditing) {
    return (
      <ListItem>
        <InlineEditInput
          type="text"
          value={editingName}
          onChange={(e) => onNameChange(e.target.value)}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSave();
            if (e.key === 'Escape') onEditCancel();
          }}
        />
        <SelectInput
          value={editingDeptId}
          onChange={(e) => onDeptChange(e.target.value)}
          style={{ maxWidth: 160 }}
        >
          <option value="">{t('部署なし', 'No department')}</option>
          {departments.map((dept) => (
            <option key={dept.id} value={dept.id}>
              {dept.name}
            </option>
          ))}
        </SelectInput>
        <ActionGroup>
          <SmallButton onClick={onSave}>
            {t('保存', 'Save')}
          </SmallButton>
          <SmallButton onClick={onEditCancel}>
            {t('キャンセル', 'Cancel')}
          </SmallButton>
        </ActionGroup>
      </ListItem>
    );
  }

  return (
    <ListItem>
      <ListItemName>{team.name}</ListItemName>
      {deptName && <ListItemSub>{deptName}</ListItemSub>}
      <ActionGroup>
        <SmallButton onClick={onEditStart}>
          {t('編集', 'Edit')}
        </SmallButton>
        <DangerButton onClick={onDelete}>
          {t('削除', 'Delete')}
        </DangerButton>
      </ActionGroup>
    </ListItem>
  );
}

export default CompanySettingsPage;
